import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { BRAND } from "../config/brand";
import { useAuth } from "../context/AuthContext";
import { useActions } from "./ActionDialogs";
import { ROLE_LABEL } from "../lib/format";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import {
  LayoutDashboard, Users, Receipt, Wallet, MoreHorizontal, Plus, Search,
  LogOut, FileText, Coins, HandCoins, X,
} from "lucide-react";
import { GlobalSearch } from "./GlobalSearch";

const NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, end: true },
  { to: "/muvekkiller", label: "Müvekkiller", icon: Users },
  { to: "/masraflar", label: "Masraflar", icon: Receipt },
  { to: "/ayarlar", label: "Daha Fazla", icon: MoreHorizontal },
];

const DESKTOP_NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, end: true },
  { to: "/muvekkiller", label: "Müvekkiller", icon: Users },
  { to: "/odemeler", label: "Ödemeler", icon: Wallet },
  { to: "/masraflar", label: "Masraflar", icon: Receipt },
  { to: "/raporlar", label: "Raporlar", icon: FileText },
  { to: "/ayarlar", label: "Ayarlar", icon: MoreHorizontal },
];

const QUICK = [
  { type: "advance", label: "Avans Ekle", icon: Coins },
  { type: "expense", label: "Masraf Ekle", icon: Receipt },
  { type: "collection", label: "Tahsilat Ekle", icon: HandCoins },
  { type: "fee", label: "Vekâlet Ücreti", icon: Wallet },
  { type: "document", label: "Belge Ekle", icon: FileText },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { open } = useActions();
  const navigate = useNavigate();
  const [fabOpen, setFabOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-40">
        <div className="px-6 py-6 border-b border-slate-100">
          <img src={BRAND.logos.horizontal} alt={BRAND.name} className="h-9 object-contain" />
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Finans Takip</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {DESKTOP_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidenav-${n.label}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-navy-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}>
              <n.icon className="w-5 h-5" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-100">
          <div className="px-3 mb-2">
            <p className="text-sm font-semibold text-navy-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{ROLE_LABEL[user?.role] || user?.role}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-brand-red hover:bg-brand-redbg w-full transition-colors">
            <LogOut className="w-5 h-5" /> Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center gap-3 px-4 md:px-8 h-16">
            <img src={BRAND.logos.compact} alt={BRAND.name} className="h-7 object-contain md:hidden" />
            <div className="flex-1" />
            <button data-testid="desktop-quick-add" onClick={() => setFabOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-red text-white text-sm font-medium hover:bg-brand-reddark active:scale-[0.98] transition-all">
              <Plus className="w-4 h-4" /> Hızlı İşlem
            </button>
            <button data-testid="global-search-btn" onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 text-slate-500 text-sm hover:bg-slate-200 transition-colors md:w-72 md:justify-start">
              <Search className="w-4 h-4" /> <span className="hidden md:inline">Ara…</span>
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 md:px-8 py-5 pb-[calc(7rem_+_env(safe-area-inset-bottom))] md:pb-8 max-w-6xl w-full">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-t border-slate-200 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 items-center h-16 relative">
          {NAV.slice(0, 2).map((n) => <NavItem key={n.to} n={n} />)}
          <div className="flex justify-center">
            <button data-testid="fab-add-action" onClick={() => setFabOpen(true)}
              className="absolute -top-6 w-14 h-14 rounded-full bg-brand-red text-white flex items-center justify-center shadow-[0_8px_16px_rgba(163,38,56,0.35)] active:scale-95 transition-transform">
              <Plus className="w-7 h-7" />
            </button>
          </div>
          {NAV.slice(2).map((n) => <NavItem key={n.to} n={n} />)}
        </div>
      </nav>

      {/* FAB action sheet */}
      <Sheet open={fabOpen} onOpenChange={setFabOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl pb-8">
          <div className="mx-auto w-10 h-1.5 rounded-full bg-slate-200 mb-4" />
          <h3 className="font-heading text-lg font-semibold text-navy-900 mb-4">Hızlı İşlem</h3>
          <div className="grid grid-cols-2 gap-3">
            {QUICK.map((q) => (
              <button key={q.type} data-testid={`quick-${q.type}`}
                onClick={() => { setFabOpen(false); open(q.type, { onDone: () => window.dispatchEvent(new Event("shb:refresh")) }); }}
                className="flex flex-col items-start gap-3 p-4 rounded-2xl border border-slate-200 hover:bg-slate-50 transition-colors active:scale-[0.98]">
                <span className="w-10 h-10 rounded-xl bg-navy-900 text-white flex items-center justify-center"><q.icon className="w-5 h-5" /></span>
                <span className="text-sm font-semibold text-navy-900">{q.label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={(id) => { setSearchOpen(false); navigate(`/muvekkiller/${id}`); }} />
    </div>
  );
}

function NavItem({ n }) {
  return (
    <NavLink to={n.to} end={n.end} data-testid={`bottomnav-${n.label}`}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 h-full text-[11px] font-medium transition-colors ${
          isActive ? "text-brand-red" : "text-slate-400"
        }`}>
      <n.icon className="w-5 h-5" />{n.label}
    </NavLink>
  );
}
